import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import parse, { domToReact } from 'html-react-parser';
import { Log } from 'helpers/errorLogger';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { i18nConfig } from 'project.config';

export interface Props {
  markdown: string;
  className?: string;
  onCollectionLinkClick?: () => void;
}

const Markdown: React.FC<Props> = ({ markdown, className, onCollectionLinkClick }) => {
  const [safeMarkdown, setSafeMarkdown] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const { locale } = useParams();

  useEffect(() => {
    if (typeof markdown === 'string') {
      setSafeMarkdown(
        DOMPurify.sanitize(markdown, {
          ALLOWED_ATTR: [
            'id',
            'class',
            'style',
            'title',
            'lang',
            'data-*',
            'href',
            'target',
            'rel',
            'name',
            'download',
            'src',
            'alt',
            'width',
            'height',
            'type',
            'value',
          ],
        }),
      );
    }
  }, [markdown]);

  const handleLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    href: string,
    target?: string
  ) => {
    if (!target || target === '_self') {
      event.preventDefault();
      const relativePathMissingLocale = i18nConfig.locales.every((locale) => !href.startsWith(`/${locale}/`) && href !== `/${locale}` );
      if (href.includes('#') || href.includes('#collection')) {
        if (onCollectionLinkClick) {
          onCollectionLinkClick();
        }
      } else if (href?.includes('https://')) {
        router.push(href);
      } else if (!relativePathMissingLocale) {
        router.push(href);
      } else {
        router.push(`/${locale}${href}`);
      }
    }
  };

  const handleLinkHover = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, href: string) => {
    // On hover, modify the href to include the locale
        event.currentTarget.setAttribute('href', href?.includes('https://')?`${href}`:`/${locale}${href}`);
  };

  const parseMarkdown = () => {
    return parse(safeMarkdown, {
      replace: (domNode: any) => {
        if (domNode.name === 'a') {
          const href = domNode.attribs.href;
          const target = domNode.attribs.target || '_self';
  
          return href?.includes('mailto') ? domToReact(domNode.children) :(
            <Link href={href} passHref legacyBehavior prefetch={false}>
                <a
                  target={target}
                  rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                  onClick={(e) => handleLinkClick(e, href, target)}
                >
                  {domToReact(domNode.children)}
                </a>
            </Link>
          );
        }
      },
    });
  };

  if (typeof markdown !== 'string') {
    Log.error(new Error(`Markdown: Invalid markdown property. Expected string but received ${typeof markdown}`));
    return null;
  }

    return <div ref={containerRef} className={className}>{parseMarkdown()}</div>;
};

export default Markdown;
